/* eslint-disable no-useless-catch */
import React, { useImperativeHandle, forwardRef, useEffect } from 'react';
import s from './s.module.less';
import { Form, Button, DatePicker, Input, message } from 'antd';
import { PlusOutlined } from '@ant-design/icons';
import FileUploadPrivate from 'components/form/FileUploadPrivate';
import StateSelect from 'components/form/StateSelect';
import { ILicenseData, FormMultipleLicense, LicenseData } from 'types/newUniprofile';
import { providerLicenseBatchUpdate } from 'api/provider';
import { formatDate } from 'utils/common';
import { License } from 'types/common';
import dayjs from 'dayjs';

interface FileUploadWrapperProps {
    value?: string;
    onChange?: (value: string) => void;
}

const FileUploadWrapper: React.FC<FileUploadWrapperProps> = ({ value, onChange }) => {
    return (
        <div className={s.uploadContainer}>
            <FileUploadPrivate
                value={value}
                onChange={onChange}
                uploadType="LICENSE"
                tips="PNG, JPG, GIF up to 10MB"
                accept="image/*,application/pdf"
            />
        </div>
    );
};

interface IProps {
    value?: License[] | null;
    onSuccess?: () => void;
    required?: boolean; // Whether all fields are required, default true for backward compatibility
}

const LicenseUploadForm = forwardRef((props: IProps, ref) => {
    const { onSuccess, value, required = true } = props;
    const [form] = Form.useForm<FormMultipleLicense>();

    useEffect(() => {
        if (value?.length) {
            form.setFieldsValue({
                licenses: value.map((item) => ({
                    ...item,
                    expireDate: item.expireDate ? dayjs(item.expireDate) : undefined,
                })),
            });
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [value]);

    // 检查是否有填写内容
    const hasContent = (): boolean => {
        const values = form.getFieldsValue() as FormMultipleLicense;
        const licenses = values.licenses || [];
        // 检查是否有任何一个 license 填写了内容
        return licenses.some((license: LicenseData) => {
            return !!(license.state || license.type || license.licenseNumber || license.expireDate || license.url);
        });
    };

    const onUpdateData = async () => {
        try {
            // 仅在非必填时，空表单才允许跳过；必填时走下方校验，空字段会拦截提交
            if (!required && !hasContent()) {
                // 如果没有填写内容，返回 true 表示可以跳过
                return true;
            }

            // 如果有填写内容，需要验证表单
            const params = await form.validateFields();
            const licenseArrary: ILicenseData[] = (params.licenses || []).map((license) => ({
                ...license,
                expireDate: formatDate(license.expireDate),
                licenseType: '',
            }));
            const result = await providerLicenseBatchUpdate(licenseArrary);
            if (result.error) {
                message.error(result.error);
                throw new Error(result.error);
            } else {
                message.success('License information has been updated');
                await onSuccess?.();
                return false; // 返回 false 表示已经提交数据
            }
        } catch (e) {
            // If not required, allow skipping validation errors
            if (!required) {
                await onSuccess?.();
                return true; // 返回 true 表示可以跳过
            }
            throw e;
        }
    };

    useImperativeHandle(ref, () => ({
        onUpdate: () => {
            return onUpdateData();
        },
        hasContent: () => {
            return hasContent();
        },
    }));

    return (
        <Form
            className={s.formSection}
            form={form}
            layout="vertical"
            initialValues={{
                licenses: [{}], // Start with one empty license
            }}
        >
            <Form.List name="licenses">
                {(fields, { add, remove }) => (
                    <div className={s.licensesSection}>
                        {fields.map((field, index) => (
                            <div key={field.key} className={s.licenseBlock}>
                                {/* License Title and Remove Button */}
                                <div className={s.licenseHeader}>
                                    <h3 className={s.licenseTitle}>Licences {index + 1}</h3>
                                    {fields.length > 1 && (
                                        <Button onClick={() => remove(field.name)} className={s.removeBtn}>
                                            Remove
                                        </Button>
                                    )}
                                </div>

                                {/* First Row: State and License Name */}
                                <div className={s.formRow}>
                                    <Form.Item
                                        {...field}
                                        name={[field.name, 'state']}
                                        label="State for licenses"
                                        rules={required ? [{ required: true, message: 'Please select a state' }] : []}
                                        required={required}
                                        className={s.formItem}
                                        validateTrigger={['onChange', 'onBlur']}
                                    >
                                        <StateSelect />
                                    </Form.Item>

                                    <Form.Item
                                        {...field}
                                        name={[field.name, 'type']}
                                        label="License name"
                                        rules={
                                            required
                                                ? [{ required: true, message: 'Please select a license type' }]
                                                : []
                                        }
                                        required={required}
                                        className={s.formItem}
                                        validateTrigger={['onChange', 'onBlur']}
                                    >
                                        <Input placeholder="Ex, Nurse practitioner," />
                                    </Form.Item>
                                </div>

                                {/* Second Row: License Number and Expiration Date */}
                                <div className={s.formRow}>
                                    <Form.Item
                                        {...field}
                                        name={[field.name, 'licenseNumber']}
                                        label="License number"
                                        rules={
                                            required ? [{ required: true, message: 'Please enter license number' }] : []
                                        }
                                        required={required}
                                        className={s.formItem}
                                        validateTrigger={['onChange', 'onBlur']}
                                    >
                                        <Input
                                            maxLength={25}
                                            className={s.inputField}
                                            placeholder="Enter license number"
                                        />
                                    </Form.Item>

                                    <Form.Item
                                        {...field}
                                        name={[field.name, 'expireDate']}
                                        label="Expiration date"
                                        rules={
                                            required
                                                ? [{ required: true, message: 'Please select expiration date' }]
                                                : []
                                        }
                                        required={required}
                                        className={s.formItem}
                                        validateTrigger={['onChange', 'onBlur']}
                                    >
                                        <DatePicker
                                            className={s.dateField}
                                            placeholder="Select expiration date"
                                            disabledDate={(date) => {
                                                const now = new Date().getTime();
                                                if (!date) {
                                                    return false;
                                                }
                                                return date?.valueOf() < now;
                                            }}
                                        />
                                    </Form.Item>
                                </div>

                                {/* Upload Section */}
                                <Form.Item
                                    {...field}
                                    name={[field.name, 'url']}
                                    label="Upload the copy of the license"
                                    rules={
                                        required ? [{ required: true, message: 'Please upload the license file' }] : []
                                    }
                                    required={required}
                                    validateTrigger={['onChange', 'onBlur']}
                                >
                                    <FileUploadWrapper />
                                </Form.Item>
                            </div>
                        ))}

                        {/* Add Another License Button */}
                        <Button onClick={() => add()} icon={<PlusOutlined />} className={s.addLicenseBtn}>
                            Add another medical license
                        </Button>
                    </div>
                )}
            </Form.List>
        </Form>
    );
});

export default LicenseUploadForm;
